// ----------------------------------------------------------------------
// Types & internal helpers
// ----------------------------------------------------------------------

export type InputStringValue = string | null | undefined;

function processInput(inputValue: InputStringValue): string | null {
  if (inputValue == null) return null;
  const str = String(inputValue);
  return str.trim();
}

// ----------------------------------------------------------------------
// Basic capitalisation variants
// ----------------------------------------------------------------------

/** Capitalise only the very first character ("hello world" → "Hello world") */
export function fCapitalize(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Capitalise the first character of *every* word ("hello world" → "Hello World") */
export function fCapitalizeWords(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str
    .split(/\s+/)
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ');
}

/** Capitalise first character of each word and subword (e.g. "o'connor" → "O'Connor") */
export function fCapitalizeWordsStrict(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str.replace(/\p{L}[\p{L}'-]*/gu, (word) =>
    word
      .split(/(['-])/)
      .map((part) =>
        part === '-' || part === "'"
          ? part
          : part.charAt(0).toUpperCase() + part.slice(1)
      )
      .join('')
  );
}

/** Sentence case: first char upper‑case, remainder lower‑case */
export function fSentenceCase(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/** Title case with English articles/prepositions excluded */
export function fTitleCase(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  const smallWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'in',
    'nor',
    'of',
    'on',
    'or',
    'per',
    'the',
    'to',
    'vs',
    'via',
  ]);
  return str
    .toLowerCase()
    .split(/\s+/)
    .map((word, idx, arr) => {
      if (smallWords.has(word) && idx !== 0 && idx !== arr.length - 1) {
        return word;
      }
      return fCapitalize(word);
    })
    .join(' ');
}

// ----------------------------------------------------------------------
// Case conversions (camel, pascal, kebab, snake)
// ----------------------------------------------------------------------

function wordsFromCamelOrDelim(str: string): string[] {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/);
}

/** camelCase */
export function fCamelCase(inputValue: InputStringValue): string {
  const raw = processInput(inputValue);
  if (!raw) return '';
  const words = wordsFromCamelOrDelim(raw.toLowerCase());
  return (
    words[0] +
    words
      .slice(1)
      .map((w) => fCapitalize(w))
      .join('')
  );
}

/** PascalCase */
export function fPascalCase(inputValue: InputStringValue): string {
  const raw = processInput(inputValue);
  if (!raw) return '';
  return wordsFromCamelOrDelim(raw.toLowerCase())
    .map((w) => fCapitalize(w))
    .join('');
}

/** kebab-case */
export function fKebabCase(inputValue: InputStringValue): string {
  const raw = processInput(inputValue);
  if (!raw) return '';
  return wordsFromCamelOrDelim(raw).join('-').toLowerCase();
}

/** snake_case */
export function fSnakeCase(inputValue: InputStringValue): string {
  const raw = processInput(inputValue);
  if (!raw) return '';
  return wordsFromCamelOrDelim(raw).join('_').toLowerCase();
}

// ----------------------------------------------------------------------
// Length helpers
// ----------------------------------------------------------------------

/** Truncate to `max` characters, optionally adding an ellipsis (default "…") */
export function fTruncate(
  inputValue: InputStringValue,
  max = 50,
  ellipsis = '…'
): string {
  const str = processInput(inputValue);
  if (!str || str.length <= max) return str ?? '';
  return str.slice(0, max).trimEnd() + ellipsis;
}

/** Truncate middle of string, keeping start & end chunks intact. Good for long hashes/IDs */
export function fEllipsisCenter(
  inputValue: InputStringValue,
  head = 6,
  tail = 4,
  ellipsis = '…'
): string {
  const str = processInput(inputValue);
  if (!str) return '';
  if (str.length <= head + tail + 3) return str; // small enough
  return `${str.slice(0, head)}${ellipsis}${str.slice(-tail)}`;
}

/**
 * Truncates the file name keeping the extension intact, placing ellipsis in the middle if too long.
 *
 * @param inputValue - The full file name (e.g., "verylongfilenamewithdetails.pdf")
 * @param maxBaseLength - Max length allowed for the base name (excluding extension), default: 20
 * @param ellipsis - Ellipsis string to use in the middle, default: "…"
 *
 * @example
 * fEllipsisFileName("verylongfilenamewithdetails.pdf", 10) → "verylong…ails.pdf"
 */
export function fEllipsisFileName(
  inputValue: InputStringValue,
  maxBaseLength = 25,
  ellipsis = '…'
): string {
  const str = processInput(inputValue);
  if (!str) return '';

  const lastDotIndex = str.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < str.length - 1;

  const base = hasExtension ? str.slice(0, lastDotIndex) : str;
  const ext = hasExtension ? str.slice(lastDotIndex) : '';

  if (base.length <= maxBaseLength) return base + ext;

  const half = Math.floor((maxBaseLength - ellipsis.length) / 2);
  const start = base.slice(0, half);
  const end = base.slice(-half);

  return `${start}${ellipsis}${end}${ext}`;
}

// ----------------------------------------------------------------------
// Misc. helpers
// ----------------------------------------------------------------------

/** Remove diacritics/accents using Unicode NFD */
export function fRemoveAccents(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Slugify: ASCII‑only, lowercase, dash‑separated – suitable for URLs */
export function fSlugify(inputValue: InputStringValue): string {
  const ascii = fRemoveAccents(inputValue);
  if (!ascii) return '';
  return ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

/** Grab the initials of the first `limit` words (default 2) */
export function fInitials(inputValue: InputStringValue, limit = 2): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str
    .split(/\s+/)
    .slice(0, limit)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

/** Basic pluraliser: adds "s", "es" or replaces trailing "y" with "ies" based on English rules */
export function fPluralize(
  inputValue: InputStringValue,
  count: number
): string {
  const word = processInput(inputValue);
  if (!word) return '';
  if (count === 1) return word;
  if (/[^aeiou]y$/i.test(word)) return word.replace(/y$/i, 'ies');
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}

/** Mask an e‑mail address, keeping first char + domain */
export function fMaskEmail(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';

  const [user, domain] = str.split('@');
  if (!user || !domain) return str;

  return `${user.charAt(0)}***@${domain}`;
}

/** Mask a phone number, keeping last 2 digits (123‑456‑7890 → ***‑***‑**90) */
export function fMaskPhone(inputValue: InputStringValue): string {
  const str = processInput(inputValue);
  if (!str) return '';
  return str.replace(/\d(?=.{2})/g, '*');
}

/** Produce a random alpha‑numeric string of `length` characters */
export function fRandomString(length = 8): string {
  const pool = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(
    { length },
    () => pool[Math.floor(Math.random() * pool.length)]
  ).join('');
}

/**
 * Safely returns a string representation of a value, or a fallback if it's null, undefined, or empty.
 *
 * Useful for displaying placeholders (like "N/A") when data is missing.
 *
 * @param value - The value to check and render.
 * @param fallback - The fallback text to return when the value is nullish or empty.
 * @returns The original value as a string, or the fallback.
 *
 * @example
 * fWithPlaceholder(user.email, 'N/A') // → 'john@example.com' or 'N/A'
 */
export function fWithPlaceholder<T>(
  value: T | null | undefined,
  fallback = 'N/A'
): string {
  return value !== null && value !== '' ? String(value) : fallback;
}

/**
 * Combines multiple string values with a separator, or returns a fallback if all are empty.
 *
 * @param values - An array of values (string, null, or undefined) to combine.
 * @param separator - Separator to use between non-empty values. Default: " - ".
 * @param fallback - Fallback string when all values are empty or nullish. Default: "N/A".
 *
 * @example
 * fCombinedWithFallback(['Alice', 'Developer']) → "Alice - Developer"
 * fCombinedWithFallback(['Alice', null]) → "Alice"
 * fCombinedWithFallback([null, undefined]) → "N/A"
 */

export function fCombinedWithPlaceholder(
  values: (string | null | undefined)[],
  separator = ' - ',
  fallback = 'N/A'
): string {
  const parts = values.map((v) => v?.trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(separator) : fallback;
}

/**
 * Formats a numeric range with a separator and optional suffix,
 * or returns a fallback if values are missing, invalid, or both zero.
 *
 * @param min - Minimum number.
 * @param max - Maximum number.
 * @param separator - Separator string between values (default: "–").
 * @param fallback - Fallback string if values are invalid or zero (default: "N/A").
 * @param suffix - Optional string to append when range is valid (e.g. "participants").
 *
 * @example
 * fRangeWithPlaceholder(3, 10, '–', 'N/A', 'participants') → "3–10 participants"
 * fRangeWithPlaceholder(0, 0, '–', 'N/A', 'participants') → "N/A"
 */
export function fRangeWithPlaceholder(
  min?: number | null,
  max?: number | null,
  separator = '–',
  fallback = 'N/A',
  suffix?: string
): string {
  const isValid = (n: unknown): n is number =>
    typeof n === 'number' && !Number.isNaN(n);

  if (isValid(min) && isValid(max) && !(min === 0 && max === 0)) {
    return `${min}${separator}${max}${suffix ? ` ${suffix}` : ''}`;
  }

  return fallback;
}

/**
 * Formats a fee and currency string, or returns a fallback if either is missing or invalid.
 *
 * @param amount - Fee amount (as string or number).
 * @param currency - Currency code or symbol.
 * @param fallback - Fallback string to return when values are invalid. Default: "N/A".
 *
 * @example
 * fAmountWithPlaceholder("100", "USD") → "100 USD"
 * fAmountWithPlaceholder(null, "USD") → "N/A"
 */
export function fAmountWithPlaceholder(
  amount?: string | number | null,
  currency?: string | null,
  fallback = 'N/A'
): string {
  const validAmount =
    typeof amount === 'number' ||
    (typeof amount === 'string' && amount.trim() !== '');
  const validCurrency = typeof currency === 'string' && currency.trim() !== '';

  return validAmount && validCurrency ? `${amount} ${currency}` : fallback;
}

/**
 * Builds a properly formatted full name from first and last names.
 *
 * If both are missing, returns a fallback (default: "N/A").
 */
export function fFullName(
  firstName?: string | null,
  lastName?: string | null,
  fallback = 'N/A'
): string {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name ? fCapitalizeWordsStrict(name) : fallback;
}

/**
 * Formats a full name by abbreviating the last name to its initial followed by a period.
 *
 * Useful for displaying shortened names in user interfaces, such as "John D." instead of "John Doe".
 *
 * - If the input contains only one word, the original name is returned unchanged.
 * - Handles multiple first name parts (e.g., "John Michael Doe" → "John Michael D.").
 *
 * @param name - The full name to abbreviate.
 * @returns A shortened version of the name with the last name reduced to its initial.
 *
 * @example
 * fAbbreviateFullName("John Doe") → "John D."
 * fAbbreviateFullName("John Michael Doe") → "John Michael D."
 * fAbbreviateFullName("Madonna") → "Madonna"
 */
export function fAbbreviateFullName(name: string): string {
  const nameParts = name.split(' ');
  if (nameParts.length < 2) return name;

  const lastName = nameParts[nameParts.length - 1];
  if (!lastName) return name;

  const lastNameInitial = `${lastName.charAt(0)}.`;
  const firstNameParts = nameParts.slice(0, -1).join(' ');

  return `${firstNameParts} ${lastNameInitial}`;
}

/** Trims a search input and returns undefined if it's empty after trimming */
export function fSanitizeSearchInput(
  inputValue: InputStringValue
): string | undefined {
  const str = processInput(inputValue);
  if (!str || str === '') return undefined;
  return str;
}

/** Returns a non-null, non-undefined string. Defaults to empty string. */
export function fNormalizeString(inputValue: InputStringValue): string {
  return typeof inputValue === 'string' ? inputValue : '';
}

/**
 * Extracts the first substring found between double quotes.
 *
 * Useful for parsing quoted values like: `The message was "important"` → "important"
 *
 * @param value - The string to search in.
 * @returns The first quoted substring, or empty string if none found.
 *
 * @example
 * extractFirstElementBetweenQuotes('This is a "quoted" word.') → "quoted"
 */
export function fExtractFirstElementBetweenQuotes(value: string): string {
  const regex = /"([^"]*)"/;
  const match = value.match(regex);
  return match?.[1] ?? '';
}
